/* OS IMAGE TOGGLE — START */
/* os-image-toggle.js
   Handles the flat lay / on model image view toggle on collection PLPs.
   To remove this feature entirely, delete this file and remove the
   {% render 'os-image-toggle' %} include from sections/collection.liquid,
   plus the <script> tag loading this file from layout/theme.liquid.
*/

(function () {
  'use strict';

  /* --- Constants --- */
  var STORAGE_KEY = 'os-image-view';
  var VIEW_FLAT = 'flat';
  var VIEW_MODEL = 'model';
  var ACTIVE_CLASS = 'is-active';

  /* --- Track current active view globally within this IIFE --- */
  var currentView = VIEW_FLAT;

  /* --- Read saved preference from localStorage --- */
  function getSavedView() {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  /* --- Save preference to localStorage --- */
  function saveView(view) {
    try {
      localStorage.setItem(STORAGE_KEY, view);
    } catch (e) {
      /* localStorage unavailable — silently continue */
    }
  }

  /* --- Update body attribute for Microsoft Clarity session segmentation --- */
  function updateBodyAttr(view) {
    document.body.setAttribute('data-os-image-view', view);
  }

  /* --- Push event to dataLayer for GA4 via GTM --- */
  function pushDataLayerEvent(view) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'image_view_toggle',
      image_view_selected: view,
      page_collection: window.__osImageToggleCollection || ''
    });
  }

  /* --- Push event to Klaviyo (identified users only) --- */
  function pushKlaviyoEvent(view) {
    if (window._learnq && typeof window._learnq.push === 'function') {
      window._learnq.push(['track', 'Image View Toggle', {
        view_selected: view,
        collection: window.__osImageToggleCollection || ''
      }]);
    }
  }

  /*
   * --- Swap images on all product cards ---
   * Image 0 = flat lay shot (default in Shopify media array)
   * Image 1 = on model shot
   * Cards marked data-os-flat-only have no second image and are skipped —
   * they remain on their flat lay image regardless of view mode.
   */
  function applyView(view) {
    currentView = view;
    var cards = document.querySelectorAll('[data-os-has-second-image]');
    cards.forEach(function (card) {
      var img0 = card.querySelector('[data-grid-image="0"]');
      var img1 = card.querySelector('[data-grid-image="1"]');
      if (!img0 || !img1) return;

      if (view === VIEW_MODEL) {
        img0.classList.remove(ACTIVE_CLASS);
        img1.classList.add(ACTIVE_CLASS);
        /* Ensure lazy image inside template tag is promoted to DOM */
        promoteTemplate(img1);
      } else {
        img1.classList.remove(ACTIVE_CLASS);
        img0.classList.add(ACTIVE_CLASS);
      }
    });
  }

  /*
   * Shopify wraps non-active images in <template> tags to prevent
   * lazy loading. When we activate image 1 programmatically we need
   * to extract the template content and replace the template node.
   */
  function promoteTemplate(wrapper) {
    var tmpl = wrapper.querySelector('template');
    if (!tmpl) return;
    var content = tmpl.content.cloneNode(true);
    wrapper.replaceChild(content, tmpl);
  }

  /*
   * --- Re-assert view state on a single card after hover ---
   * The theme's hover/slideshow JS resets the active image on mouseenter.
   * We listen for mouseleave on each card and re-apply our current view,
   * so the card returns to the correct image once the user stops hovering.
   * We use mouseleave rather than fighting mouseenter timing.
   */
  function bindHoverCorrection(card) {
    card.addEventListener('mouseleave', function () {
      if (currentView === VIEW_MODEL) {
        var img0 = card.querySelector('[data-grid-image="0"]');
        var img1 = card.querySelector('[data-grid-image="1"]');
        if (!img0 || !img1) return;
        img0.classList.remove(ACTIVE_CLASS);
        img1.classList.add(ACTIVE_CLASS);
      }
      /* In flat view the theme default is correct — no correction needed */
    });
  }

  /* --- Update toggle button active states --- */
  function updateToggleUI(view) {
    var toggles = document.querySelectorAll('[data-os-toggle]');
    toggles.forEach(function (el) {
      var btnFlat = el.querySelector('[data-os-view="flat"]');
      var btnModel = el.querySelector('[data-os-view="model"]');
      if (!btnFlat || !btnModel) return;

      if (view === VIEW_MODEL) {
        btnFlat.classList.remove('os-toggle__btn--active');
        btnModel.classList.add('os-toggle__btn--active');
        btnFlat.setAttribute('aria-pressed', 'false');
        btnModel.setAttribute('aria-pressed', 'true');
      } else {
        btnModel.classList.remove('os-toggle__btn--active');
        btnFlat.classList.add('os-toggle__btn--active');
        btnFlat.setAttribute('aria-pressed', 'true');
        btnModel.setAttribute('aria-pressed', 'false');
      }
    });
  }

  /* --- Handle a toggle click --- */
  function handleToggleClick(view) {
    applyView(view);
    updateToggleUI(view);
    saveView(view);
    updateBodyAttr(view);
    pushDataLayerEvent(view);
    pushKlaviyoEvent(view);
  }

  /*
   * --- Re-apply state to whatever is currently in the DOM ---
   * Split out of init() so it can be called again after AJAX pagination
   * or filtering replaces the product grid. Safe to call repeatedly.
   */
  function syncToDom() {
    var toggleContainer = document.querySelector('[data-os-toggle]');
    if (!toggleContainer) return;

    var defaultView = toggleContainer.getAttribute('data-os-default') || VIEW_FLAT;
    var savedView = getSavedView();
    var activeView = (savedView === VIEW_FLAT || savedView === VIEW_MODEL) ? savedView : defaultView;

    applyView(activeView);
    updateToggleUI(activeView);
    updateBodyAttr(activeView);

    /* Bind hover correction to any card that has not been bound yet */
    var cards = document.querySelectorAll('[data-os-has-second-image]');
    cards.forEach(function (card) {
      if (card.__osHoverBound) return;
      card.__osHoverBound = true;
      bindHoverCorrection(card);
    });
  }

  /*
   * --- Watch the grid for AJAX replacement ---
   * Pagination and filtering re-render the product grid in place
   * (pagination-custom is rendered with enable_ajax: true), which
   * destroys the cards this script has already processed and leaves
   * the new ones on their default image. A childList observer on the
   * grid container catches every one of those swaps. Debounced so a
   * single re-render triggers one sync, not one per appended node.
   */
  function observeGrid() {
    if (typeof MutationObserver === 'undefined') return;

    var grid = document.querySelector('[data-grid-pagination]')
      || document.querySelector('.collection__products')
      || document.querySelector('[data-section-type="collection"]');
    if (!grid) return;

    var pending = null;
    var observer = new MutationObserver(function () {
      if (pending) clearTimeout(pending);
      pending = setTimeout(function () {
        pending = null;
        syncToDom();
      }, 60);
    });

    observer.observe(grid, { childList: true, subtree: true });
  }

  /* --- Initialise --- */
  function init() {
    if (!document.querySelector('[data-os-toggle]')) return; /* Toggle not present on this collection */

    syncToDom();

    /*
     * Click handling is delegated to the document rather than bound to
     * each button, so the toggle keeps working after the nav bar is
     * re-rendered by AJAX pagination or filtering.
     */
    document.addEventListener('click', function (event) {
      var btn = event.target.closest && event.target.closest('[data-os-toggle] [data-os-view]');
      if (!btn) return;
      handleToggleClick(btn.getAttribute('data-os-view'));
    });

    observeGrid();
  }

  /* Run on DOMContentLoaded */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
/* OS IMAGE TOGGLE — END */
